import { useEffect } from 'react';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';

import { ListNode, ListItemNode, $createListItemNode, $createListNode } from '@lexical/list';
import { $getRoot, $createTextNode, $createParagraphNode } from 'lexical';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';

import ToolbarPlugin from './toolbarplugin';
import { RichTextEditorThemeConfig } from '../utils/commonutils';

const editorConfig = {
    namespace: 'React.js Demo',
    nodes: [
        ListNode,
        ListItemNode,
    ],
    onError(error: Error) {
        throw error;
    },
    theme: RichTextEditorThemeConfig,
};

const RichTextField = ({
    label,
    value,
    onChange,
    error,
    isDisabled = false,
    isRequired = false,
    id,
    initialValue = '',
    placeholder = ''
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    error?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    id: string;
    initialValue?: string;
    placeholder?: string;
}) => (
    <div className="flex flex-col gap-2">
        <label className="text-sm text-default-600 font-medium" htmlFor={id}>
            {label}{isRequired && <span className="text-danger">*</span>}
        </label>
        <LexicalComposer initialConfig={{ ...editorConfig, editable: !isDisabled }}>
            <Richtexteditor
                getInputText={onChange}
                initialValue={initialValue}
                placeholder={placeholder}
            />
        </LexicalComposer>
        {error && (
            <span className="text-danger text-sm">
                {error}
            </span>
        )}
    </div>
);

function Richtexteditor(props: Readonly<{
    getInputText: (inputText: string) => void,
    initialValue?: string,
    getJsonState?: (jsonState: string) => void,
    placeholder?: string
}>) {
    const { getInputText, initialValue = '', getJsonState, placeholder = '' } = props;
    const [editor] = useLexicalComposerContext();

    // Text parsing utilities
    const parseHtmlTag = (text: string, i: number): { content: string; format: number; newIndex: number } | null => {
        if (text[i] !== '<') return null;

        const tagEnd = text.indexOf('>', i);
        if (tagEnd === -1) return null;

        const tag = text.slice(i + 1, tagEnd).toLowerCase();
        const closingTag = `</${tag}>`;
        const closingTagIndex = text.indexOf(closingTag, tagEnd + 1);

        if (closingTagIndex === -1) return null;

        const content = text.slice(tagEnd + 1, closingTagIndex);
        let format = 0;

        if (tag === 'b' || tag === 'strong') format |= 1;
        else if (tag === 'i' || tag === 'em') format |= 2;
        else if (tag === 'u') format |= 4;
        else if (tag === 's' || tag === 'strike') format |= 8;

        return { content, format, newIndex: closingTagIndex + closingTag.length };
    };

    const parseMarkdownFormat = (text: string, i: number): { format: number; newIndex: number } | null => {
        const markdownPatterns = [
            { pattern: '**', format: 1 },
            { pattern: '~~', format: 8 },
            { pattern: '__', format: 4 }
        ];

        for (const { pattern, format } of markdownPatterns) {
            if (text.slice(i, i + pattern.length) === pattern) {
                return { format, newIndex: i + pattern.length };
            }
        }

        if (text[i] === '*' && text[i + 1] !== '*') {
            return { format: 2, newIndex: i + 1 };
        }

        return null;
    };

    // Simplified text parsing function
    const parseFormattedText = (text: string) => {
        const segments: Array<{ text: string; format: number }> = [];
        let currentText = '';
        let currentFormat = 0;
        let i = 0;

        while (i < text.length) {
            const htmlResult = parseHtmlTag(text, i);
            if (htmlResult) {
                if (currentText) {
                    segments.push({ text: currentText, format: currentFormat });
                    currentText = '';
                }

                const nestedSegments = parseFormattedTextSegments(htmlResult.content);
                nestedSegments.forEach(segment => {
                    segment.format |= htmlResult.format;
                    segments.push(segment);
                });

                i = htmlResult.newIndex;
                continue;
            }

            const markdownResult = parseMarkdownFormat(text, i);
            if (markdownResult) {
                if (currentText) {
                    segments.push({ text: currentText, format: currentFormat });
                    currentText = '';
                }
                currentFormat ^= markdownResult.format;
                i = markdownResult.newIndex;
                continue;
            }

            currentText += text[i];
            i++;
        }

        if (currentText) {
            segments.push({ text: currentText, format: currentFormat });
        }

        return createTextNodesFromSegments(segments, text);
    };

    // Create text nodes from parsed segments
    const createTextNodesFromSegments = (segments: Array<{ text: string; format: number }>, originalText: string) => {
        if (segments.length === 0) {
            return $createTextNode(originalText);
        }

        if (segments.length === 1) {
            const textNode = $createTextNode(segments[0].text);
            textNode.setFormat(segments[0].format);
            return textNode;
        }

        const paragraphNode = $createParagraphNode();
        segments.forEach(segment => {
            if (segment.text.trim()) {
                const textNode = $createTextNode(segment.text);
                textNode.setFormat(segment.format);
                paragraphNode.append(textNode);
            }
        });

        return paragraphNode;
    };

    // Simplified markdown segments parser
    const parseFormattedTextSegments = (text: string): Array<{ text: string; format: number }> => {
        const segments: Array<{ text: string; format: number }> = [];
        let currentText = '';
        let currentFormat = 0;
        let i = 0;

        while (i < text.length) {
            const markdownResult = parseMarkdownFormat(text, i);
            if (markdownResult) {
                if (currentText) {
                    segments.push({ text: currentText, format: currentFormat });
                    currentText = '';
                }
                currentFormat ^= markdownResult.format;
                i = markdownResult.newIndex;
                continue;
            }

            currentText += text[i];
            i++;
        }

        if (currentText) {
            segments.push({ text: currentText, format: currentFormat });
        }

        return segments;
    };

    // Extract list item creation logic
    const createListItem = (line: string) => {
        const listItemText = line.replace(/^[•\-*]\s/, '').replace(/^\d+\.\s/, '');
        const listItemNode = $createListItemNode();
        const textNode = $createTextNode(listItemText);
        listItemNode.append(textNode);
        return listItemNode;
    };

    // Extract list node management
    const getOrCreateListNode = (root: any) => {
        let listNode = root.getLastChild();
        if (!listNode || listNode.getType() !== 'list') {
            listNode = $createListNode('bullet');
            root.append(listNode);
        }
        return listNode;
    };

    // Extract line processing logic
    const processLine = (line: string, root: any) => {
        if (!line.trim()) return;

        if (/^[•\-*]\s/.exec(line) || /^\d+\.\s/.exec(line)) {
            const listItemNode = createListItem(line);
            const listNode = getOrCreateListNode(root);
            if (listNode.getType() === 'list') {
                (listNode as ListNode).append(listItemNode);
            }
        } else {
            const formattedText = parseFormattedText(line);
            if (formattedText.getType() === 'paragraph') {
                root.append(formattedText);
            } else {
                const paragraphNode = $createParagraphNode();
                paragraphNode.append(formattedText);
                root.append(paragraphNode);
            }
        }
    };

    useEffect(() => {
        if (!initialValue) return;

        editor.update(() => {
            const root = $getRoot();
            root.clear();

            const lines = initialValue.split('\n');
            lines.forEach(line => processLine(line, root));
        });
    }, [initialValue, editor]);

    // Text formatting utilities
    const formatTextWithMarkdown = (text: string, format: number): string => {
        const formatMap = [
            { flag: 8, prefix: '~~', suffix: '~~' },
            { flag: 1, prefix: '**', suffix: '**' },
            { flag: 2, prefix: '*', suffix: '*' },
            { flag: 4, prefix: '__', suffix: '__' }
        ];

        let formattedText = text;
        formatMap.forEach(({ flag, prefix, suffix }) => {
            if (format & flag) {
                formattedText = `${prefix}${formattedText}${suffix}`;
            }
        });

        return formattedText;
    };

    const processTextNode = (node: any): string => {
        let result = '';
        const children = node.getChildren();

        children.forEach((child: any) => {
            if (child.getType() === 'text') {
                const text = child.getTextContent();
                const format = child.getFormat();
                result += formatTextWithMarkdown(text, format);
            } else if (child.getType() === 'paragraph') {
                result += processTextNode(child);
            }
        });

        return result;
    };

    // Extract paragraph processing
    const processParagraph = (child: any): string => {
        const paragraphText = processTextNode(child);
        return paragraphText.trim() ? paragraphText + '\n' : '';
    };

    // Extract list processing
    const processList = (child: any): string => {
        let listContent = '';
        const listItems = child.getChildren();

        listItems.forEach((listItem: any, index: number) => {
            const itemText = processTextNode(listItem);
            if (itemText.trim()) {
                const bullet = child.getListType() === 'number' ? `${index + 1}. ` : '• ';
                listContent += bullet + itemText + '\n';
            }
        });

        return listContent;
    };

    // Extract child processing
    const processChild = (child: any): string => {
        if (child.getType() === 'paragraph') {
            return processParagraph(child);
        } else if (child.getType() === 'list') {
            return processList(child);
        }
        return '';
    };

    // Extract content generation
    const getFormattedContent = (editorState: any) => {
        let formattedContent = '';

        editorState.read(() => {
            const root = $getRoot();
            const children = root.getChildren();

            children.forEach((child: any) => {
                formattedContent += processChild(child);
            });
        });

        return formattedContent.trim();
    };

    // Extract JSON state generation
    const getEditorStateAsJSON = (editorState: any) => {
        return JSON.stringify(editorState.toJSON());
    };

    // Extract change handler
    const handleEditorChange = (editorState: any) => {
        editorState.read(() => {
            const formattedContent = getFormattedContent(editorState);
            getInputText(formattedContent);

            if (getJsonState) {
                const jsonState = getEditorStateAsJSON(editorState);
                getJsonState(jsonState);
            }
        });
    };

    return (
        <div className="editor-container">
            <ToolbarPlugin />
            <div className="editor-inner">
                <RichTextPlugin
                    contentEditable={
                        <ContentEditable
                            className="editor-input"
                            aria-placeholder={placeholder}
                            placeholder={
                                <div className="editor-placeholder">{placeholder}</div>
                            }
                        />
                    }
                    ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin />
                <ListPlugin />
                <OnChangePlugin onChange={handleEditorChange} />
            </div>
        </div>
    );
}

export { RichTextField };